import React, { useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  CheckCircle, 
  Star, 
  ExpandMore, 
  AccessTime, 
  School, 
  Assessment, 
  WorkOutline, 
  Psychology, 
  CommentBank, 
  Email, 
  ListAlt,
  MenuBook,
  LiveHelp,
  PersonSearch,
  LinkedIn,
  EmojiEvents,
  RouteOutlined,
  Person
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';

const Zelfcoaching = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { language } = useLanguage();
  const location = useLocation();
  const pakkettenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if URL has #pakketten hash or scrollTo=pakketten parameter
    if (location.hash === '#pakketten' || location.search.includes('scrollTo=pakketten')) {
      // Scroll to the pakketten section with a slight delay for smoother experience
      setTimeout(() => {
        if (pakkettenRef.current) {
          pakkettenRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [location]);

  return (
    <>
      <PageHeader
        title="Zelfcoaching"
        subtitle="Ontdek hoe je zonder hulp grote stappen kunt zetten in jouw professionele ontwikkeling."
      />

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ mb: 6 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body1" fontWeight="medium">Wat is zelfcoaching en hoe werkt het?</Typography>
            </AccordionSummary>
            <AccordionSummary>
              <Typography variant="body1" paragraph>
                Met mijn zelfcoaching programma krijg je toegang tot uitgebreide zelfreflectie oefeningen, praktische tools en exclusieve content die je helpt om zelfstandig te werken aan je professionele ontwikkeling. Geen dure coaching sessies, maar effectieve methoden die je in je eigen tempo kunt toepassen. Of je nu aan het begin van je loopbaan staat, een carrièreswitch overweegt of gewoon het maximale uit je huidige rol wilt halen - deze zelfcoaching materialen geven je de inzichten en vaardigheden die je nodig hebt om weloverwogen keuzes te maken en je doelen te bereiken.
              </Typography>
            </AccordionSummary>
          </Accordion>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Wat kun je verwachten?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Psychology color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Zelfinzicht
                  </Typography>
                </Box>
                <Typography variant="body1">
                  Uitgebreide oefeningen en assessments die je helpen om je sterke punten, waarden en drijfveren te ontdekken. Deze vormen de basis voor elke carrièrebeslissing die je neemt.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <RouteOutlined color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Carrièreplanning
                  </Typography>
                </Box>
                <Typography variant="body1">
                  Praktische tools en stappenplannen voor het definiëren van je carrièredoelen, het maken van een actieplan en het consistent werken aan je professionele ontwikkeling.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WorkOutline color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Sollicitatievaardigheden
                  </Typography>
                </Box>
                <Typography variant="body1">
                  Templates en handleidingen voor het maken van een overtuigend CV, een krachtige motivatiebrief en een professioneel LinkedIn-profiel.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccessTime color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Persoonlijke effectiviteit
                  </Typography>
                </Box>
                <Typography variant="body1">
                  Technieken voor timemanagement, productiviteit, communicatie en andere vaardigheden die je helpen excelleren in je huidige of toekomstige rol.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4, textAlign: 'center' }} id="pakketten" ref={pakkettenRef}>
          <Typography variant="h4" component="h2" gutterBottom>
            Kies jouw pakket
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', mb: 2 }}>
            Selecteer het pakket dat het beste bij jouw behoeften past en start vandaag nog met je persoonlijke ontwikkeling.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              boxShadow: 3,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6
              }
            }}>
              <Box sx={{ 
                bgcolor: theme.palette.primary.light, 
                p: 3, 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 110 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MenuBook color="inherit" sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h5" component="h3" fontWeight={600} color="white">
                    Essential
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="white" sx={{ opacity: 0.9, fontSize: '0.95rem' }}>
                  Jouw eerste stap naar groei
                </Typography>
              </Box>
              
              <CardContent sx={{ flexGrow: 1, p: 2, pt: 3, display: 'flex', flexDirection: 'column' }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <MenuBook color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Basis zelfcoaching materialen" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <School color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Zelfstudie in eigen tempo" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ListAlt color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Digitale werkbladen" />
                  </ListItem>
                </List>
                
                <Box sx={{ flexGrow: 1 }} />
                
                <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2 }}>
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                          Voor wie?
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                        Beginners die zelf aan de slag willen met hun persoonlijke ontwikkeling en basisinzichten willen opdoen.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                        Bekijk alle details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
                        • Fundamentele zelfcoachings-opdrachten voor persoonlijke groei
                        <br />
                        • Laagdrempelige start met coaching, perfecte introductie
                        <br />
                        • 3 maanden toegang tot het online platform
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Box sx={{ width: '100%', mb: 1, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    €29
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {language === 'nl' ? 'totaal incl. BTW' : 'total incl. VAT'}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ bgcolor: theme.palette.primary.light, '&:hover': { bgcolor: theme.palette.primary.main } }}
                  onClick={() => navigate('/login?tab=register')}
                >
                  Ontgrendel Essential
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              boxShadow: 4,
              border: `2px solid #D4AF37`,
              position: 'relative',
              zIndex: 1,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6
              }
            }}>
              <Box sx={{ 
                position: 'absolute',
                top: 15,
                right: -30,
                bgcolor: theme.palette.warning.main,
                color: 'white',
                py: 0.5,
                px: 3,
                transform: 'rotate(45deg)',
                boxShadow: 1,
                zIndex: 1,
                fontWeight: 'bold'
              }}>
                POPULAIR
              </Box>
              
              <Box sx={{ 
                bgcolor: '#D4AF37', 
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 110
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Assessment color="inherit" sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h5" component="h3" fontWeight={600}>
                    Gold
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="white" sx={{ opacity: 0.9, fontSize: '0.95rem' }}>
                  Ontwikkel jezelf volledig
                </Typography>
              </Box>
              
              <CardContent sx={{ flexGrow: 1, p: 2, pt: 3, display: 'flex', flexDirection: 'column' }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <MenuBook sx={{ color: '#D4AF37' }} />
                    </ListItemIcon>
                    <ListItemText primary="Alles uit Essential" secondary="plus meer" secondaryTypographyProps={{ sx: { color: 'text.secondary', fontSize: '0.8rem' } }} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Assessment sx={{ color: '#D4AF37' }} />
                    </ListItemIcon>
                    <ListItemText primary="Complete zelfcoaching bibliotheek" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WorkOutline sx={{ color: '#D4AF37' }} />
                    </ListItemIcon>
                    <ListItemText primary="Sollicitatie handleidingen" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LiveHelp sx={{ color: '#D4AF37' }} />
                    </ListItemIcon>
                    <ListItemText primary="Maandelijkse Q&A sessies" />
                  </ListItem>
                </List>
                
                <Box sx={{ flexGrow: 1 }} />
                
                <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2 }}>
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person fontSize="small" sx={{ mr: 1, color: '#D4AF37' }} />
                        <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                          Voor wie?
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                        Je wilt je ontwikkeling versnellen en bent klaar voor diepgaande zelfreflectie. Je zoekt concrete tools en advies voor je persoonlijke en professionele groei.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                        Bekijk alle details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
                        • Alle voordelen van Essential
                        <br />
                        • Volledige zelfcoachings-opdrachten inclusief carrière testen
                        <br />
                        • Uitgebreide sollicitatie handleidingen van CV tot arbeidsvoorwaarden
                        <br />
                        • Maandelijkse online Q&A sessies met experts
                        <br />
                        • Ondersteuning tussen sessies via e-mail
                        <br />
                        • 6 maanden toegang tot het online platform
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Box sx={{ width: '100%', mb: 1, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    €149
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    excl. btw
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ bgcolor: '#D4AF37', '&:hover': { bgcolor: '#BF9D30' } }}
                  onClick={() => navigate('/login?tab=register')}
                >
                  Ontgrendel Gold
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              boxShadow: 3,
              border: `2px solid #E0BFB8`,
              position: 'relative',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6
              }
            }}>
              
              <Box sx={{ 
                bgcolor: '#E0BFB8', 
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 110
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmojiEvents color="inherit" sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h5" component="h3" fontWeight={600}>
                    Elite
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="white" sx={{ opacity: 0.9, fontSize: '0.95rem' }}>
                  Excelleer in je carrière
                </Typography>
              </Box>
              
              <CardContent sx={{ flexGrow: 1, p: 2, pt: 3, display: 'flex', flexDirection: 'column' }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <MenuBook sx={{ color: '#E0BFB8' }} />
                    </ListItemIcon>
                    <ListItemText primary="Alles uit Gold" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PersonSearch sx={{ color: '#E0BFB8' }} />
                    </ListItemIcon>
                    <ListItemText primary="Persoonlijke assessment analyse" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LinkedIn sx={{ color: '#E0BFB8' }} />
                    </ListItemIcon>
                    <ListItemText primary="CV & LinkedIn review" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmojiEvents sx={{ color: '#E0BFB8' }} />
                    </ListItemIcon>
                    <ListItemText primary="Geavanceerde carrièrestrategie" />
                  </ListItem>
                </List>
                
                <Box sx={{ flexGrow: 1 }} />
                
                <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2 }}>
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person fontSize="small" sx={{ mr: 1, color: '#E0BFB8' }} />
                        <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                          Voor wie?
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                        Je bent serieus over je carrière en wilt doelgericht aan je ontwikkeling werken. Met gepersonaliseerde feedback en strategieën kun jij je volledige potentieel benutten.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                        Bekijk alle details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
                        • Alle voordelen van Gold
                        <br />
                        • Complete zelfcoachings-bibliotheek met alle premium materialen
                        <br />
                        • Professionele sollicitatie templates en adviezen
                        <br />
                        • Maandelijkse Q&A sessies met directe toegang tot experts
                        <br />
                        • Persoonlijke analyse op basis van assessment resultaten
                        <br />
                        • CV & LinkedIn review met professionele feedback
                        <br />
                        • Uitgebreide ondersteuning tussen sessies
                        <br />
                        • 9 maanden toegang tot het online platform
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Box sx={{ width: '100%', mb: 1, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    €499
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    totaal incl. BTW
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ bgcolor: '#E0BFB8', '&:hover': { bgcolor: '#C9A9A3' } }}
                  onClick={() => navigate('/login?tab=register')}
                >
                  Ontgrendel Elite
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: 4,
              border: `2px solid #7D93A4`,
              position: 'relative',
              zIndex: 1,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6
              }
            }}>
              
              <Box sx={{ 
                background: 'linear-gradient(145deg, #7D93A4 0%, #A5B6C5 100%)', 
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 110
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Psychology color="inherit" sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h5" component="h3" fontWeight={600}>
                    Platinum
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="white" sx={{ opacity: 0.9, fontSize: '0.95rem' }}>
                  Bereik je volledige potentieel
                </Typography>
              </Box>
              
              <CardContent sx={{ flexGrow: 1, p: 2, pt: 3, display: 'flex', flexDirection: 'column' }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <MenuBook sx={{ color: '#7D93A4' }} />
                    </ListItemIcon>
                    <ListItemText primary="Alles uit Elite" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CommentBank sx={{ color: '#7D93A4' }} />
                    </ListItemIcon>
                    <ListItemText primary="10 coaching sessies (1 uur)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Psychology sx={{ color: '#7D93A4' }} />
                    </ListItemIcon>
                    <ListItemText primary="Ontwikkelingsplan op maat" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ListAlt sx={{ color: '#7D93A4' }} />
                    </ListItemIcon>
                    <ListItemText primary="Directe toegang tot alle content" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Email sx={{ color: '#7D93A4' }} />
                    </ListItemIcon>
                    <ListItemText primary="WhatsApp ondersteuning" />
                  </ListItem>
                </List>
                
                <Box sx={{ flexGrow: 1 }} />
                
                <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2 }}>
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person fontSize="small" sx={{ mr: 1, color: '#7D93A4' }} />
                        <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                          Voor wie?
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                        Je zoekt gerichte begeleiding bij het bereiken van je doelen en wilt een coach die met je meedenkt. Je bent bereid te investeren in jezelf voor blijvende persoonlijke ontwikkeling.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion elevation={0}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      sx={{ minHeight: '36px', py: 0, px: 0 }}
                    >
                      <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                        Bekijk alle details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
                        • 10 persoonlijke coaching sessies (online of op locatie)
                        <br />
                        • Alle Elite voordelen met volledige toegang
                        <br />
                        • Persoonlijk ontwikkelingsplan op maat gemaakt
                        <br />
                        • Exclusieve coaching tools voor cliënten
                        <br />
                        • Prioriteit ondersteuning via WhatsApp tussen sessies
                        <br />
                        • 12 maanden toegang tot het online platform
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Box sx={{ width: '100%', mb: 1, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    €990
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {language === 'nl' ? 'totaal incl. BTW' : 'total incl. VAT'}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ bgcolor: '#7D93A4', '&:hover': { bgcolor: '#A5B6C5' } }}
                  onClick={() => navigate('/login?tab=register')}
                >
                  Start Platinum
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        {/* Frequently Asked Questions Section */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
            Veelgestelde vragen
          </Typography>
          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="medium">Hoe lang heb ik toegang tot de zelfcoaching materialen?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    De toegangsduur verschilt per pakket: Essential (3 maanden), Gold (6 maanden), 
                    Elite (9 maanden) en Platinum (12 maanden). Na deze periode kun je 
                    je abonnement verlengen of upgraden.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="medium">Kan ik later upgraden naar een hoger pakket?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Ja, dat kan! Je kunt op elk moment upgraden naar een uitgebreider pakket. 
                    Bij een upgrade ontvang je korting op basis van je huidige pakket. Neem contact op 
                    voor de mogelijkheden.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="medium">Hoe werkt de ondersteuning tussen sessies?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Afhankelijk van je pakket kun je tussen sessies vragen stellen via e-mail of WhatsApp. 
                    Bij Gold Premium kun je vragen stellen via e-mail, bij Elite Premium is er uitgebreidere 
                    ondersteuning, en bij Platinum krijg je prioriteit ondersteuning via WhatsApp.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="medium">Wat is het verschil tussen de pakketten?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Elk pakket bouwt voort op het vorige: Essential biedt basismateriaal, Gold voegt 
                    daar uitgebreide content en Q&A sessies aan toe, Elite bevat persoonlijke 
                    analyses en CV reviews, en Platinum biedt een volledig 
                    één-op-één traject met 10 sessies en alle premium materialen.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="medium">Hoe begin ik met zelfcoaching?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Na aanmelding krijg je direct toegang tot alle materialen van jouw gekozen pakket. 
                    Je kunt meteen starten met de zelfreflectie oefeningen en het werken aan je persoonlijke 
                    en professionele ontwikkeling, volledig in je eigen tempo.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="medium">Is zelfcoaching geschikt voor iedereen?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Zelfcoaching is ideaal voor mensen die gemotiveerd zijn om aan zichzelf te werken, 
                    over zelfreflectie beschikken, en in staat zijn zelf stappen te zetten. Als je liever 
                    persoonlijke begeleiding hebt, is het Platinum pakket wellicht meer geschikt.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ textAlign: 'center', p: 4, bgcolor: theme.palette.grey[100], borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom>
            Klaar om aan de slag te gaan met zelfcoaching?
          </Typography>
          <Typography variant="body1" paragraph>
            Kies het pakket dat bij jou past en begin vandaag nog met het ontdekken van je potentieel.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/login?tab=register')}
          >
            Maak een account
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default Zelfcoaching; 